// [목적] 뱃지 메달 1개를 그리는 표시 전용 컴포넌트 — 육각 메달 + 등급 그라디언트 + 라인 아이콘(+ 잠금).
// [이유] 2026-09-21 badge-catalog: 기존 뱃지 UI 는 이모지 한 글자(🏅/🌊/⑂)였다. OS·폰트마다 모양과
//        굵기가 달라 3열 그리드에 늘어놓으면 톤이 제각각이고, 미획득 상태를 표현할 방법도 없었다
//        (LineIcons.tsx 헤더의 "이모지 → 라인 아이콘" 정리 흐름과 동일한 이유).
// [사이드 이팩트] 없음(순수 표시). 색은 packages/ui badge 토큰만 사용(CLAUDE.md §6-3).
// [수정 시 고려사항] 아이콘 path 는 utils/badgeCatalog.ts BADGE_ICON_PATHS 가 소유 — 여기에 추가하지 말 것.
//                   육각 클립은 ring(바깥) / face(안쪽) 두 겹이 같은 clipPath 를 공유해야 테두리가 고르게 보인다.

import { Box } from '@mui/material';

import {
  BADGE_LOCK_ICON_PATH,
  BADGE_TIER_PALETTE,
  getBadgeIconPath,
  type BadgeMedalPalette,
} from '../../utils/badgeCatalog.js';
import type { BadgeCatalogItem } from '@gangwoncanvas/types';

/** 육각 메달 실루엣 — 위/아래가 뾰족한 hex(포인티 탑) */
const HEX_CLIP = 'polygon(50% 0%, 96% 25%, 96% 75%, 50% 100%, 4% 75%, 4% 25%)';

export interface BadgeMedalProps {
  kind: string;
  tier: BadgeCatalogItem['tier'];
  /** 미획득이면 회색 메달 + 자물쇠 */
  locked?: boolean;
  /** 메달 한 변의 폭(px) — 그리드 64, 상세 다이얼로그 96 */
  size?: number;
}

export function BadgeMedal({
  kind,
  tier,
  locked = false,
  size = 64,
}: BadgeMedalProps): JSX.Element {
  const palette: BadgeMedalPalette = locked ? BADGE_TIER_PALETTE.locked : BADGE_TIER_PALETTE[tier];
  const iconSize = Math.round(size * 0.46);
  const lockSize = Math.round(size * 0.3);

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      {/* ring — 메달 테두리(바깥 육각) */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: palette.ring,
          clipPath: HEX_CLIP,
          transition: 'transform .18s ease',
        }}
      />
      {/* face — 등급 그라디언트(안쪽 육각) + 상단 광택 */}
      <Box
        sx={{
          position: 'absolute',
          inset: `${Math.max(2, Math.round(size * 0.045))}px`,
          clipPath: HEX_CLIP,
          background: `linear-gradient(150deg, ${palette.bgFrom} 0%, ${palette.bgTo} 100%)`,
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(160deg, rgba(255,255,255,.55) 0%, rgba(255,255,255,0) 55%)',
          },
        }}
      />
      {/* 아이콘 */}
      <Box
        component="svg"
        viewBox="0 0 24 24"
        sx={{
          position: 'relative',
          width: iconSize,
          height: iconSize,
          fill: 'none',
          stroke: palette.fg,
          strokeWidth: 1.8,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          opacity: locked ? 0.75 : 1,
        }}
      >
        <path d={getBadgeIconPath(kind)} />
      </Box>
      {/* 잠금 표시 — 미획득 메달 우하단 */}
      {locked && (
        <Box
          sx={{
            position: 'absolute',
            right: `-${Math.round(size * 0.04)}px`,
            bottom: `${Math.round(size * 0.04)}px`,
            width: lockSize,
            height: lockSize,
            borderRadius: '50%',
            bgcolor: palette.bgTo,
            border: `1px solid ${palette.ring}`,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 24 24"
            sx={{
              width: Math.round(lockSize * 0.62),
              height: Math.round(lockSize * 0.62),
              fill: 'none',
              stroke: palette.fg,
              strokeWidth: 2,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
            }}
          >
            <path d={BADGE_LOCK_ICON_PATH} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
